import React, { useState, useEffect } from "react";
import {
  GetFollowUpCountByLeadId,
  FollowUpCountResponse,
} from "../Services/APIService";

interface FollowUpTypesOverviewProps {
  leadId: number;
  refreshTasks: boolean; // Add prop for task refresh
  onTasksRefreshed: () => void; // Add prop to reset refresh state
}

export interface FollowUpTypeCount {
  followUpTypeId: number;
  followUpTypeName: string;
  count: number;
}

const FollowUpTypesOverview: React.FC<FollowUpTypesOverviewProps> = ({
  leadId,
  refreshTasks,
  onTasksRefreshed,
}) => {
  const [followUpData, setFollowUpData] =
    useState<FollowUpCountResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFollowUpData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await GetFollowUpCountByLeadId(leadId);
      setFollowUpData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching follow-up data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leadId) {
      fetchFollowUpData();
    }
  }, [leadId]);

  useEffect(() => {
    if (refreshTasks && leadId) {
      fetchFollowUpData().then(() => {
        onTasksRefreshed(); // Reset refresh state after fetching
      });
    }
  }, [refreshTasks, leadId]);

  if (loading) {
    return (
      <div className="border border-gray-300 rounded-md p-4">
        <div className="text-gray-600 text-sm font-medium mb-3">
          Lead Follow-up Overview (Completed)
        </div>
        <div className="text-center text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-gray-300 rounded-md p-4">
        <div className="text-gray-600 text-sm font-medium mb-3">
          Lead Follow-up Overview (Completed)
        </div>
        <div className="text-center text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!followUpData || !followUpData.followUpTypeCounts) {
    return (
      <div className="border border-gray-300 rounded-md p-4">
        <div className="text-gray-600 text-sm font-medium mb-3">
          Lead Follow-up Overview (Completed)
        </div>
        <div className="text-center text-gray-500">No data available</div>
      </div>
    );
  }

  const followUpTypes = followUpData.followUpTypeCounts.filter(
    (f) => f.followUpTypeId != 0 && f.followUpTypeId != 1
  );

  return (
    <div className="card" style={{ backgroundColor: "#e8dce1" }}>
      <div
        className="card-header carHeaderWithBtn"
        style={{ backgroundColor: "#e8dce1" }}
      >
        <h5 className="card-title mb-0">Lead Follow-up Overview (Completed)</h5>
      </div>
      <div className="m-2 ">
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                {followUpTypes.map((item) => (
                  <th key={item.followUpTypeId} className="text-center">
                    {item.followUpTypeName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {followUpTypes.map((item) => (
                  <td
                    key={item.followUpTypeId}
                    className="text-center fw-semibold"
                  >
                    {item.count}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FollowUpTypesOverview;
